import jwtDecode from "jwt-decode";
import { create } from "zustand";

import { catchAsync } from "@/v1/utils/errorHandler";
import { queryClient } from "@/v1/utils/queryClient";
import { postTokenToParent } from "@/v2/utils/postTokenToParent";

import { loginService } from "../service/authService";
import * as service from "../service/teamMembersService";
import { IJwtMember } from "../types/Auth/IJwtMember.interface";
import { IMember } from "../types/TeamMembers/Member.interface";
import { addBearerToken } from "../utils/apiClient";
import { authentication } from "../utils/authentication";

import { clearPersistedImpersonation } from "./useUserStore/utils/clearPersistedImpersonation";
import { deriveActiveUserState } from "./useUserStore/utils/deriveActiveUserState";
import { persistImpersonation } from "./useUserStore/utils/persistImpersonation";
import { resolveRestorableImpersonation } from "./useUserStore/utils/resolveRestorableImpersonation";

interface IUseUserStore {
  user?: IJwtMember | null;
  impersonatedUser?: IJwtMember | null;
  activeUser?: IJwtMember | null;
  activeRoles: string[];
  activePermissions: string[];
  isOwner: boolean;
  login: () => Promise<void | boolean>;
  logout: () => Promise<void>;
  impersonateUser: (member: IMember) => Promise<void>;
  removeImpersonatedUser: () => void;
}

export const useUserStore = create<IUseUserStore>((set, get) => {
  let activeUserJwt = "";

  return {
    user: null,
    impersonatedUser: null,
    ...deriveActiveUserState(null, null),
    impersonateUser: catchAsync(async (member: IMember) => {
      const { data } = await service.impersonateUser(member.id);

      queryClient.clear();

      const impersonatedUser = jwtDecode(data.jwt) as IJwtMember;

      persistImpersonation(data.jwt, get().user?.sub);
      addBearerToken(data.jwt);

      set((state) => ({
        impersonatedUser,
        ...deriveActiveUserState(state.user, impersonatedUser),
      }));
    }),
    removeImpersonatedUser: () => {
      queryClient.clear();

      clearPersistedImpersonation();
      addBearerToken(activeUserJwt);

      set((state) => ({
        impersonatedUser: null,
        ...deriveActiveUserState(state.user, null),
      }));
    },
    logout: catchAsync(async () => {
      clearPersistedImpersonation();

      set({ user: null, impersonatedUser: null, ...deriveActiveUserState(null, null) });

      authentication.logout();
    }),
    login: catchAsync(async () => {
      const oktaLocalData = localStorage.getItem("okta-token-storage");

      if (oktaLocalData) {
        const oktaData = JSON.parse(oktaLocalData);

        addBearerToken(oktaData.accessToken?.value);

        const res = await loginService();

        activeUserJwt = res.data.jwt;

        postTokenToParent(res.data.jwt);

        const user = jwtDecode(res.data.jwt) as IJwtMember;
        const restorable = resolveRestorableImpersonation(user);
        const impersonatedUser = restorable?.user ?? null;

        addBearerToken(restorable ? restorable.jwt : res.data.jwt);

        queryClient.clear();

        set({
          user,
          impersonatedUser,
          ...deriveActiveUserState(user, impersonatedUser),
        });

        return true;
      }
    }),
  };
});
